import moment from 'moment';

/** Five-day bands by closure date day-of-month (non-overlapping). */
export const M_DAY_BUCKETS = [
    { key: 'd5', label: 'D-5', minDay: 1, maxDay: 5, headerBg: '#FFE4CC' },
    { key: 'd10', label: 'D-10', minDay: 6, maxDay: 10, headerBg: '#FFF9C4' },
    { key: 'd15', label: 'D-15', minDay: 11, maxDay: 15, headerBg: '#C8E6C9' },
    { key: 'd20', label: 'D-20', minDay: 16, maxDay: 20, headerBg: '#F8BBD0' },
    { key: 'd25', label: 'D-25', minDay: 21, maxDay: 25, headerBg: '#FFE0B2' },
    { key: 'd30', label: 'D-30', minDay: 26, maxDay: 30, headerBg: '#FFF59D' },
];

export const M_DAY_31_BUCKET = {
    key: 'd31',
    label: 'D-31',
    minDay: 31,
    maxDay: 31,
    headerBg: '#FFF59D',
};

/**
 * Table columns: D-5 … D-30, plus D-31 when any lead closes on the 31st.
 */
export const getMDayBucketDisplayLabels = (leads = []) => {
    const hasDay31 = leads.some((item) => {
        if (!item?.closureDate) return false;
        return moment(item.closureDate).date() === 31;
    });
    return hasDay31 ? [...M_DAY_BUCKETS, M_DAY_31_BUCKET] : [...M_DAY_BUCKETS];
};

/** Single bucket key for closure day (1–5 → d5, 6–10 → d10, …, 26–30 → d30, 31 → d31). */
export const getDiscreteMDayBucketKey = (closureDate, buckets = M_DAY_BUCKETS) => {
    if (!closureDate) return null;
    const day = moment(closureDate).date();
    if (!Number.isFinite(day) || day < 1) return null;
    const match = buckets.find(({ minDay, maxDay }) => day >= minDay && day <= maxDay);
    return match?.key || null;
};

/** @deprecated Use getDiscreteMDayBucketKey — kept for any stale imports. */
export const getCumulativeMDayBucketKeys = (closureDate) => {
    const key = getDiscreteMDayBucketKey(closureDate, M_DAY_BUCKETS);
    return key ? [key] : [];
};

const emptyMDayBucket = () => ({ count: 0, value: 0, data: [] });

const createEmptyRow = (experienceCenter, bucketKeys) => {
    const row = { experienceCenter, total: emptyMDayBucket() };
    bucketKeys.forEach((key) => {
        row[key] = emptyMDayBucket();
    });
    return row;
};

/** Detail lists: earliest committed closure date first, then project no. */
export const sortMDayLeadsByClosureDate = (leads = []) =>
    [...leads].sort((a, b) => {
        const ta = a?.closureDate ? moment(a.closureDate).valueOf() : Number.MAX_SAFE_INTEGER;
        const tb = b?.closureDate ? moment(b.closureDate).valueOf() : Number.MAX_SAFE_INTEGER;
        if (ta !== tb) return ta - tb;
        return String(a?.erpProjectNo || '').localeCompare(String(b?.erpProjectNo || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    });

const sortExperienceCentreRows = (rows = []) =>
    [...rows].sort((a, b) => {
        if (a.experienceCenter === 'Unknown') return 1;
        if (b.experienceCenter === 'Unknown') return -1;
        return a.experienceCenter.localeCompare(b.experienceCenter, undefined, { sensitivity: 'base' });
    });

const sortRowBucketData = (row, bucketKeys) => {
    bucketKeys.forEach((key) => {
        if (row[key]) row[key].data = sortMDayLeadsByClosureDate(row[key].data);
    });
    row.total.data = sortMDayLeadsByClosureDate(row.total.data);
    return row;
};

export const buildMDayExperienceCenterBreakdown = (leads = []) => {
    const buckets = getMDayBucketDisplayLabels(leads);
    const bucketKeys = buckets.map((b) => b.key);

    const ecMap = new Map();

    leads.forEach((item) => {
        const bucketKey = getDiscreteMDayBucketKey(item?.closureDate, buckets);
        if (!bucketKey) return;

        const ecName = item?.experienceCenterName || 'Unknown';
        if (!ecMap.has(ecName)) {
            ecMap.set(ecName, createEmptyRow(ecName, bucketKeys));
        }

        const row = ecMap.get(ecName);
        const value = Number(item?.staticSalesWonBudget) || 0;

        row[bucketKey].count += 1;
        row[bucketKey].value += value;
        row[bucketKey].data.push(item);

        row.total.count += 1;
        row.total.value += value;
        row.total.data.push(item);
    });

    const rows = sortExperienceCentreRows([...ecMap.values()].map((row) => sortRowBucketData(row, bucketKeys)));

    const grandTotal = createEmptyRow('Total', bucketKeys);

    rows.forEach((row) => {
        bucketKeys.forEach((key) => {
            grandTotal[key].count += row[key].count;
            grandTotal[key].value += row[key].value;
            grandTotal[key].data.push(...row[key].data);
        });
        grandTotal.total.count += row.total.count;
        grandTotal.total.value += row.total.value;
        grandTotal.total.data.push(...row.total.data);
    });

    [...rows, grandTotal].forEach((row) => {
        bucketKeys.forEach((key) => {
            row[key].value = parseFloat(row[key].value.toFixed(2));
        });
        row.total.value = parseFloat(row.total.value.toFixed(2));
    });

    if (rows.length > 0) {
        rows.push(sortRowBucketData(grandTotal, bucketKeys));
    }

    return rows;
};

export const extractMColumnLeads = (designData = [], salesMonth = 'all') => {
    if (salesMonth === 'all') {
        const seen = new Set();
        const merged = [];
        designData
            .filter((row) => row?.month !== 'Total')
            .forEach((row) => {
                (row?.m?.data || []).forEach((item) => {
                    const id = item?._id?.toString();
                    if (id && !seen.has(id)) {
                        seen.add(id);
                        merged.push(item);
                    }
                });
            });
        return merged;
    }

    const row = designData.find((r) => r?.month === salesMonth);
    return row?.m?.data || [];
};

export const buildDesignHealthMDaySummaryQuery = ({
    storeDateFilter,
    selectedExperienceCenter,
    selectedLocation,
    userDetails,
    selectedMonthType,
    salesMonth = 'all',
}) => {
    const params = new URLSearchParams({
        startDate: storeDateFilter?.startDate || '',
        endDate: storeDateFilter?.endDate || '',
        selectedExperienceCenter: (selectedExperienceCenter || []).map((el) => el._id).join(','),
        selectedLocation: (selectedLocation || []).map((el) => el._id).join(','),
        userId: userDetails?._id || '',
        userExpeCenterId: (userDetails?.experienceCenterId || []).join(',') || '',
        monthType: selectedMonthType?.id || '',
        salesMonth: salesMonth || 'all',
    });
    return params.toString();
};

const resolveUserName = (userRef) => {
    if (userRef == null) return null;
    if (typeof userRef === 'string') return userRef.trim() || null;
    if (typeof userRef === 'object' && userRef.name) return String(userRef.name);
    return null;
};

/** Current stage from design-health-commitment-month lead rows (flat or nested). */
export const resolveLeadCurrentStage = (item) => {
    const stage =
        item?.currentStage ??
        item?.leadId?.currentStage ??
        item?.lead?.currentStage;
    if (stage == null || stage === '') return 'N/A';
    return String(stage);
};

export const resolveLeadDesignerName = (item) =>
    resolveUserName(item?.designUser) ??
    resolveUserName(item?.leadId?.designUser) ??
    item?.designerName ??
    'N/A';

export const resolveLeadOdmManagerName = (item) =>
    resolveUserName(item?.designManager) ??
    resolveUserName(item?.leadId?.designManager) ??
    item?.odmManagerName ??
    'N/A';

export const resolveLeadCityAgmName = (item) =>
    resolveUserName(item?.designAGM) ??
    resolveUserName(item?.leadId?.designAGM) ??
    item?.cityAgmName ??
    'N/A';

/**
 * Whole days from committed closure date through today.
 * Future or same-day commitment → 0; past commitment → days overdue.
 */
export const daysLapsedFromCommittedDate = (closureDate, today = moment()) => {
    if (!closureDate) return null;
    const committed = moment(closureDate).startOf('day');
    if (!committed.isValid()) return null;
    const asOf = moment(today).startOf('day');
    const diff = asOf.diff(committed, 'days');
    return diff > 0 ? diff : 0;
};

export const formatDaysLapsedFromCommittedDate = (closureDate, today = moment()) => {
    const days = daysLapsedFromCommittedDate(closureDate, today);
    if (days === null) return 'N/A';
    return days;
};

/** From design-health-commitment-month API (`daysLapsedInCurrentDesignStage`). */
export const formatDaysLapsedInCurrentDesignStage = (item) => {
    const days = item?.daysLapsedInCurrentDesignStage;
    if (days === 0) return 0;
    if (days != null && days !== '') return days;
    return 'N/A';
};

/** Modal/CSV cells: keep numeric 0 visible (avoid `value || 'NA'` treating 0 as empty). */
export const designHealthDetailCellValue = (value) => {
    if (value === 0) return 0;
    if (value == null || value === '') return 'N/A';
    return value;
};

export const parseDesignHealthMDayUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        startDate: params.get('startDate') || '',
        endDate: params.get('endDate') || '',
        selectedExperienceCenter: params.get('selectedExperienceCenter') || '',
        selectedLocation: params.get('selectedLocation') || '',
        userId: params.get('userId') || '',
        userExpeCenterId: params.get('userExpeCenterId') || '',
        monthType: params.get('monthType') || '',
        salesMonth: params.get('salesMonth') || 'all',
    };
};
